import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { NoAccess } from "../equipment/shared/NoAccess";
import AddMasterInline from "./AddMasterInline";
import { MANAGE_ROLES, VIEW_ROLES, LEGACY_PART, partOptions, toOptions, useServiceMasters } from "./shared";

const blankLine = () => ({ key: Math.random().toString(36).slice(2), line_id: null, service_type_id: "", interval_hours: "", grade_id: "", part_code_id: "", part_code: "" });

export default function SchedulePage() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const { types, grades, parts, reload } = useServiceMasters();
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentType, setEquipmentType] = useState("");
  const [lines, setLines] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const allowed = VIEW_ROLES.includes(user?.role);

  useEffect(() => {
    if (!allowed) return;
    client.get("/service-tracker/equipment-types").then((r) => { setEquipmentTypes(r.data); setEquipmentType(r.data[0]); }).catch((e) => toast.error(getErrorMessage(e)));
  }, [allowed]);

  const load = (et = equipmentType) => {
    if (!et) return;
    setLoading(true);
    client.get("/service-tracker/schedule", { params: { equipment_type: et } })
      .then((r) => {
        const rows = r.data.lines.map((l) => ({ ...l, key: l.line_id, interval_hours: String(l.interval_hours), part_code_name: l.part_code, part_code_id: l.part_code_id || "", part_code: l.part_code_legacy ? l.part_code : "" }));
        setLines(rows);
        setOriginal(rows.map((r2) => r2.line_id));
      })
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [equipmentType]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return <NoAccess />;

  const setLine = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removedCount = original.filter((id) => !lines.some((l) => l.line_id === id)).length;

  const save = async () => {
    setConfirmOpen(false);
    for (const [i, l] of lines.entries()) {
      if (!l.service_type_id) return toast.error(`Line ${i + 1}: Service Type is required`);
      if (!(Number(l.interval_hours) > 0)) return toast.error(`Line ${i + 1}: Interval (hours) is required`);
      if (!l.grade_id) return toast.error(`Line ${i + 1}: Grade is required`);
    }
    setSaving(true);
    try {
      const res = await client.put("/service-tracker/schedule", {
        lines: lines.map((l) => ({ line_id: l.line_id, service_type_id: l.service_type_id, interval_hours: Number(l.interval_hours), grade_id: l.grade_id, part_code_id: l.part_code_id || null, part_code: l.part_code_id ? null : (l.part_code || null) })),
      }, { params: { equipment_type: equipmentType } });
      toast.success(res.data.lines_removed ? `Schedule saved — ${res.data.lines_removed} line(s) and ${res.data.quantities_removed} asset quantities removed` : "Schedule saved");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="service-schedule-page">
      <div>
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Service Schedule</h2>
        <p className="text-sm text-slate-500">One shared schedule per equipment type — applies to every asset of that type. {!canManage && "View only."}</p>
      </div>

      <div className="max-w-xs">
        <label className="text-sm font-medium text-slate-700 mb-1.5 block">Equipment Type</label>
        <Select value={equipmentType} onValueChange={setEquipmentType}>
          <SelectTrigger data-testid="schedule-equipment-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {equipmentTypes.map((t) => <SelectItem key={t} value={t} data-testid={`schedule-equipment-type-option-${t}`}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <Table data-testid="schedule-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[28%]">Service Type *</TableHead>
              <TableHead className="w-[16%]">Interval (hours) *</TableHead>
              <TableHead className="w-[28%]">Grade *</TableHead>
              <TableHead className="w-[20%]">Part Code</TableHead>
              {canManage && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : lines.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400" data-testid="schedule-empty">No schedule lines yet.</TableCell></TableRow>
            ) : lines.map((l, i) => (
              <TableRow key={l.key} data-testid={`schedule-row-${i}`}>
                <TableCell>
                  {canManage ? (
                    <div className="flex gap-1.5">
                      <SearchableSelect testId={`schedule-type-select-${i}`} value={l.service_type_id} onValueChange={(v) => setLine(l.key, { service_type_id: v })} placeholder="Select"
                        options={toOptions(types, `schedule-type-${i}`)} />
                      <AddMasterInline kind="service-types" noun="Service Type" testId={`schedule-type-add-${i}`} onAdded={(n) => { reload(); setLine(l.key, { service_type_id: n.id }); }} />
                    </div>
                  ) : l.service_type}
                </TableCell>
                <TableCell>
                  {canManage ? <Input type="number" min="1" step="any" data-testid={`schedule-interval-input-${i}`} value={l.interval_hours} onChange={(e) => setLine(l.key, { interval_hours: e.target.value })} /> : l.interval_hours}
                </TableCell>
                <TableCell>
                  {canManage ? (
                    <div className="flex gap-1.5">
                      <SearchableSelect testId={`schedule-grade-select-${i}`} value={l.grade_id} onValueChange={(v) => setLine(l.key, { grade_id: v })} placeholder="Select" options={toOptions(grades, `schedule-grade-${i}`)} />
                      <AddMasterInline kind="grades" noun="Grade" testId={`schedule-grade-add-${i}`} onAdded={(n) => { reload(); setLine(l.key, { grade_id: n.id }); }} />
                    </div>
                  ) : l.grade}
                </TableCell>
                <TableCell>
                  {canManage ? (
                    <div className="flex gap-1.5">
                      <SearchableSelect testId={`schedule-partcode-select-${i}`} value={l.part_code_id || (l.part_code ? LEGACY_PART : "")} placeholder="Optional"
                        onValueChange={(v) => v !== LEGACY_PART && setLine(l.key, { part_code_id: v, part_code: "" })}
                        options={partOptions(parts, `schedule-partcode-${i}`, !l.part_code_id ? l.part_code : "", !!(l.part_code_id || l.part_code))} />
                      <AddMasterInline kind="part-codes" noun="Part Code" testId={`schedule-partcode-add-${i}`} onAdded={(n) => { reload(); setLine(l.key, { part_code_id: n.id, part_code: "" }); }} />
                    </div>
                  ) : (l.part_code_name || "—")}
                </TableCell>
                {canManage && (
                  <TableCell>
                    <Button type="button" size="icon" variant="ghost" data-testid={`schedule-remove-${i}`} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {canManage && !loading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Button type="button" variant="outline" size="sm" data-testid="schedule-add-line-button" onClick={() => setLines((ls) => [...ls, blankLine()])}>
                    <Plus className="h-4 w-4 mr-1.5" /> Add line
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {canManage && (
        <Button disabled={saving || loading} data-testid="schedule-save-button" className="bg-amber-600 hover:bg-amber-700" onClick={() => (removedCount ? setConfirmOpen(true) : save())}>
          {saving ? "Saving..." : "Save Schedule"}
        </Button>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="schedule-remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removedCount} schedule line(s)?</AlertDialogTitle>
            <AlertDialogDescription>Removing a line also removes its per-asset quantities for every rig. Past service entries are kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="schedule-remove-confirm-cancel">Go back</AlertDialogCancel>
            <AlertDialogAction data-testid="schedule-remove-confirm-ok" className="bg-red-600 hover:bg-red-700" onClick={save}>Remove and save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
